import { useMemo } from 'react';

import { ChevronDownIcon, ClockIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { AuditLog } from '@/shared/types/auditLogs';
import { DATE_BUCKET_LABEL_KEY } from '@/util/dateUtils';

import ActivityGroupRow from './ActivityGroupRow';
import { bucketActivityGroups, groupActivityLogs } from './activityLogGrouping';
import { useActivityLogLookups } from './useActivityLogLookups';

interface Props {
  /** Flat audit logs (across all loaded pages); the feed handles grouping + bucketing. */
  logs: AuditLog[];
  hideAgentLink?: boolean;
  /** Cap the number of groups shown (e.g. Home cards). */
  limit?: number;
  isLoading?: boolean;
  emptyMessage?: string;
  emptySubMessage?: string;
  /** When provided together with `hasMore`, renders a "Show more" button. */
  onShowMore?: () => void;
  hasMore?: boolean;
}

// Mirrors a feed row: timestamp gutter (w-24) + avatar + one-line sentence.
const renderSkeletons = (n = 5) => (
  <div className="flex w-full max-w-4xl flex-col">
    {Array(n)
      .fill(0)
      .map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="flex pt-3 pb-4" key={i}>
          <div className="w-24 shrink-0 pt-1 pr-2">
            <Skeleton className="h-3.5 w-10" />
          </div>
          <div className="flex min-h-7 min-w-0 flex-1 items-center gap-1">
            <Skeleton className="h-5 w-5 shrink-0 rounded-full" />
            <Skeleton className="h-4 w-full max-w-sm" />
          </div>
        </div>
      ))}
  </div>
);

/** Groups logs by target + actor within a 6h window, buckets them by day, and renders each group as a collapsible row. */
export const ActivityFeed = ({
  logs,
  hideAgentLink = false,
  limit,
  isLoading = false,
  emptyMessage,
  emptySubMessage,
  onShowMore,
  hasMore = false,
}: Props) => {
  const { t } = useTranslation();
  // Built once for the whole feed; every row shares these resolvers.
  const lookups = useActivityLogLookups();

  const buckets = useMemo(() => {
    const groups = groupActivityLogs(logs);
    const visible = limit ? groups.slice(0, limit) : groups;
    return bucketActivityGroups(visible);
  }, [logs, limit]);

  if (isLoading) {
    return renderSkeletons();
  }

  if (buckets.length === 0 && !hasMore) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <ClockIcon aria-hidden />
          </EmptyMedia>
          <EmptyTitle>{emptyMessage}</EmptyTitle>
          {emptySubMessage && <EmptyDescription>{emptySubMessage}</EmptyDescription>}
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="flex w-full max-w-4xl flex-col gap-8">
      {buckets.map((bucket, bucketIndex) => (
        <section key={bucket.key} className="flex flex-col">
          {bucketIndex > 0 && (
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-stroke" />
              <span className="text-xs text-foreground-muted">
                {t(DATE_BUCKET_LABEL_KEY[bucket.key])}
              </span>
              <div className="h-px flex-1 bg-stroke" />
            </div>
          )}
          {bucket.groups.map((group) => (
            <div key={group.log_id} className="pt-3 pb-4">
              <ActivityGroupRow group={group} lookups={lookups} hideAgentLink={hideAgentLink} />
            </div>
          ))}
        </section>
      ))}

      {onShowMore && hasMore && (
        <Button
          variant="ghost-neutral"
          size="sm"
          onClick={onShowMore}
          className="mx-auto mt-4 text-foreground-muted hover:text-foreground"
        >
          {t('activityLogs.show_more')}
          <ChevronDownIcon size={16} />
        </Button>
      )}
    </div>
  );
};

export default ActivityFeed;
